import { getCurrentUserId } from '../utils/auth_utils.js';
import { getCurrentLanguage, interpolate } from '../translations.js';
import { showToast } from '../utils/toast.js';
import { getReportReasonsForKind } from './reportReasons.js';
import { blockContent, reportContent } from './moderationApi.js';
import { markContentBlocked } from './blockedContentStore.js';

const MENU_CLASS = 'content-moderation-menu';
let activeMenu = null;
let dismissBound = false;

const isInsideModerationUi = (target) => {
  if (!(target instanceof Element)) return false;
  return !!(target.closest('.content-moderation-menu') || target.closest('.description-tooltip-menu-btn'));
};

const bindGlobalDismiss = () => {
  if (dismissBound) return;
  dismissBound = true;
  document.addEventListener('click', (event) => {
    if (!activeMenu) return;
    if (isInsideModerationUi(event.target)) return;
    closeContentModerationMenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeMenu) closeContentModerationMenu();
  });
};

const moderationCopy = (key, fallback = '', replacements = {}) => {
  const lang = getCurrentLanguage();
  const rankings = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
  const value = rankings[key] || fallback || key;
  return interpolate(value, replacements);
};

const requireLogin = async () => {
  if (getCurrentUserId()) return true;
  try {
    if (window.loginModalController?.show) {
      await window.loginModalController.show(false, moderationCopy(
        'rankings.moderation.loginRequired',
        'Log in to report or block content'
      ));
      return false;
    }
    const { default: loginModalController } = await import('../components/LoginModalController.js');
    await loginModalController?.show?.(false, moderationCopy(
      'rankings.moderation.loginRequired',
      'Log in to report or block content'
    ));
  } catch (error) {
    console.warn('[contentModerationMenu] Failed to open login modal:', error);
  }
  return false;
};

export const closeContentModerationMenu = () => {
  if (!activeMenu) return;
  activeMenu.remove();
  activeMenu = null;
};

const positionMenu = (menu, anchorRect) => {
  const menuRect = menu.getBoundingClientRect();
  let top = anchorRect.bottom + 6;
  let left = anchorRect.right - menuRect.width;
  if (left < 8) left = 8;
  if (left + menuRect.width > window.innerWidth - 8) {
    left = Math.max(8, window.innerWidth - menuRect.width - 8);
  }
  if (top + menuRect.height > window.innerHeight - 8) {
    top = Math.max(8, anchorRect.top - menuRect.height - 6);
  }
  menu.style.top = `${top}px`;
  menu.style.left = `${left}px`;
};

const createMenuItem = (action, label, iconClass) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'content-moderation-menu-item';
  button.setAttribute('data-action', action);
  button.setAttribute('role', 'menuitem');
  button.tabIndex = 0;
  const icon = document.createElement('i');
  icon.className = iconClass;
  icon.setAttribute('aria-hidden', 'true');
  const text = document.createElement('span');
  text.textContent = label;
  button.appendChild(icon);
  button.appendChild(text);
  return button;
};

const renderReasonMenu = (anchorEl, { contentKind, contentId }) => {
  closeContentModerationMenu();
  const menu = document.createElement('div');
  menu.className = MENU_CLASS;
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', moderationCopy('rankings.moderation.report', 'Report'));

  const prompt = document.createElement('div');
  prompt.className = 'content-moderation-menu-reason-label';
  prompt.textContent = moderationCopy('rankings.moderation.chooseReason', 'Why are you reporting this?');
  menu.appendChild(prompt);

  getReportReasonsForKind(contentKind).forEach((code) => {
    menu.appendChild(createMenuItem(
      `reason:${code}`,
      moderationCopy(`rankings.moderation.reasons.${code}`, code.replace(/_/g, ' ')),
      'fa-solid fa-flag'
    ));
  });

  document.body.appendChild(menu);
  positionMenu(menu, anchorEl.getBoundingClientRect());
  requestAnimationFrame(() => menu.classList.add('show'));
  activeMenu = menu;
  bindGlobalDismiss();

  const handleReasonClick = async (event) => {
    const item = event.target.closest('[data-action]');
    if (!item || !menu.contains(item)) return;
    event.preventDefault();
    event.stopPropagation();
    const action = item.getAttribute('data-action') || '';
    if (!action.startsWith('reason:')) return;
    const reasonCode = action.slice('reason:'.length);
    if (reasonCode === 'other') {
      renderOtherReasonForm(anchorEl, { contentKind, contentId });
      return;
    }
    await submitReport({ contentKind, contentId, reasonCode, reasonText: null });
  };

  menu.addEventListener('click', handleReasonClick);
};

const renderOtherReasonForm = (anchorEl, { contentKind, contentId }) => {
  closeContentModerationMenu();
  const menu = document.createElement('div');
  menu.className = MENU_CLASS;
  menu.setAttribute('role', 'dialog');
  menu.setAttribute('aria-label', moderationCopy('rankings.moderation.reasons.other', 'Other'));

  const label = document.createElement('label');
  label.className = 'content-moderation-menu-reason-label';
  label.setAttribute('for', 'content-moderation-other-reason');
  label.textContent = moderationCopy('rankings.moderation.otherPrompt', 'Describe the issue');
  menu.appendChild(label);

  const textarea = document.createElement('textarea');
  textarea.id = 'content-moderation-other-reason';
  textarea.className = 'content-moderation-other-input';
  textarea.maxLength = 500;
  textarea.rows = 3;
  textarea.setAttribute('aria-label', moderationCopy('rankings.moderation.otherPrompt', 'Describe the issue'));
  menu.appendChild(textarea);

  const submit = createMenuItem(
    'submit_other',
    moderationCopy('rankings.moderation.submitReport', 'Submit report'),
    'fa-solid fa-paper-plane'
  );
  menu.appendChild(submit);

  document.body.appendChild(menu);
  positionMenu(menu, anchorEl.getBoundingClientRect());
  requestAnimationFrame(() => menu.classList.add('show'));
  activeMenu = menu;
  bindGlobalDismiss();
  textarea.focus();

  submit.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const reasonText = textarea.value.trim();
    if (!reasonText) {
      showToast(moderationCopy('rankings.moderation.otherRequired', 'Please describe the issue'), 'warning');
      return;
    }
    await submitReport({ contentKind, contentId, reasonCode: 'other', reasonText });
  });
};

const submitReport = async ({ contentKind, contentId, reasonCode, reasonText }) => {
  try {
    await reportContent({ contentKind, contentId, reasonCode, reasonText });
    showToast(moderationCopy('rankings.moderation.reportThanks', 'Thanks, we received your report'), 'success');
    closeContentModerationMenu();
  } catch (error) {
    if (error?.code === 'ALREADY_REPORTED') {
      showToast(moderationCopy('rankings.moderation.alreadyReported', 'You already reported this'), 'info');
      closeContentModerationMenu();
      return;
    }
    showToast(error?.message || moderationCopy('rankings.moderation.reportFailed', 'Could not send report'), 'error');
  }
};

const handleBlock = async ({ contentKind, contentId }) => {
  try {
    await blockContent({ contentKind, contentId });
    markContentBlocked(contentKind, contentId);
    showToast(moderationCopy('rankings.moderation.blocked', 'This content is now hidden for you'), 'success');
    closeContentModerationMenu();
    document.querySelector('.description-tooltip')?.remove();
  } catch (error) {
    showToast(error?.message || moderationCopy('rankings.moderation.blockFailed', 'Could not block this content'), 'error');
  }
};

export const openContentModerationMenu = async (anchorEl, { contentKind, contentId }) => {
  if (!anchorEl || (contentKind !== 'character' && contentKind !== 'scenario') || contentId == null) return;
  if (activeMenu) closeContentModerationMenu();
  if (!(await requireLogin())) return;

  const menu = document.createElement('div');
  menu.className = MENU_CLASS;
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', moderationCopy('rankings.moderation.actions', 'Content actions'));
  menu.appendChild(createMenuItem(
    'report',
    moderationCopy('rankings.moderation.report', 'Report'),
    'fa-solid fa-flag'
  ));
  menu.appendChild(createMenuItem(
    'block',
    moderationCopy('rankings.moderation.block', 'Block'),
    'fa-solid fa-ban'
  ));

  document.body.appendChild(menu);
  positionMenu(menu, anchorEl.getBoundingClientRect());
  requestAnimationFrame(() => menu.classList.add('show'));
  activeMenu = menu;
  bindGlobalDismiss();

  menu.addEventListener('click', async (event) => {
    const item = event.target.closest('[data-action]');
    if (!item || !menu.contains(item)) return;
    event.preventDefault();
    event.stopPropagation();
    const action = item.getAttribute('data-action');
    if (action === 'report') {
      renderReasonMenu(anchorEl, { contentKind, contentId });
      return;
    }
    if (action === 'block') {
      await handleBlock({ contentKind, contentId });
    }
  });
};

export const appendModerationKebab = (headerEl, { contentKind, contentId }) => {
  if (!headerEl || contentId == null) return null;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'description-tooltip-menu-btn';
  button.setAttribute('aria-label', moderationCopy('rankings.moderation.actions', 'Content actions'));
  button.setAttribute('aria-haspopup', 'menu');
  button.innerHTML = '<i class="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>';
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    void openContentModerationMenu(button, { contentKind, contentId });
  });
  button.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    void openContentModerationMenu(button, { contentKind, contentId });
  });
  headerEl.appendChild(button);
  return button;
};
